/**
 * RNC — admin dashboard landing.
 *
 * Shows the template library summary, rate-limit snapshot, and a quick link to
 * the delivery log + test-send surfaces. Read-only for Supervisor/Manager.
 */
import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import { BookOpen, FlaskConical, History } from '@retail-ops/panel-shell/fi';
import { Button, KpiTile, Pill } from '@/components/primitives';
import { useAuth } from '@/contexts/AuthContext';
import { useConfig } from '@/contexts/ConfigContext';
import { canEditRnc, canReadRnc } from './roles';
import RateLimitPanel from './RateLimitPanel';

export default function RncDashboard(): JSX.Element {
  const { user } = useAuth();
  const { config } = useConfig();
  const navigate = useNavigate();

  const canRead = canReadRnc(user?.primaryRole);
  const canEdit = canEditRnc(user?.primaryRole);

  const counts = useMemo(() => {
    const all = config.notificationTemplates?.templates ?? [];
    return {
      total: all.length,
      active: all.filter((t) => t.isActive).length,
      byChannel: {
        email: all.filter((t) => t.channel === 'email').length,
        teams: all.filter((t) => t.channel === 'teams').length,
        sms: all.filter((t) => t.channel === 'sms').length,
        physical: all.filter((t) => t.channel === 'physical').length,
      },
    };
  }, [config.notificationTemplates?.templates]);

  if (!canRead) {
    return (
      <div className="rnc-page">
        <div className="rnc-banner rnc-banner--err">
          You don't have access to the Notifications Center. Request the
          <code>Compliance</code> or <code>SuperAdmin</code> role.
        </div>
      </div>
    );
  }

  return (
    <div className="rnc-page">
      <header className="rnc-page__header">
        <div className="rnc-page__title-row">
          <h1 className="rnc-page__title">Notifications center</h1>
          <Pill variant="muted">Module RNC</Pill>
          <Pill variant={canEdit ? 'ok' : 'warn'}>
            {canEdit ? 'Editor' : 'Read-only'}
          </Pill>
        </div>
        <p className="rnc-page__subtitle">
          Outbound notification spine — email, Teams, SMS, and physical. Templates
          live in SQL; every outbound is logged for 7 years with a SHA-256 hash.
        </p>
      </header>

      <div className="rnc-grid">
        <section className="rnc-panel">
          <h2 className="rnc-panel__title">Template library</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 'var(--sp-5)' }}>
            <KpiTile label="Total" value={counts.total} />
            <KpiTile label="Active" value={counts.active} severity="approvals" />
            <KpiTile label="Email" value={counts.byChannel.email} />
            <KpiTile label="SMS" value={counts.byChannel.sms} />
          </div>
          <Button
            variant="primary"
            size="sm"
            startIcon={<BookOpen size={14} />}
            onClick={() => navigate('/rnc/templates')}
          >
            Open template library
          </Button>
        </section>

        <RateLimitPanel />

        <section className="rnc-panel">
          <h2 className="rnc-panel__title">Quick actions</h2>
          <Button
            variant="ghost"
            startIcon={<History size={14} />}
            onClick={() => navigate('/rnc/log')}
          >
            Delivery log
          </Button>
          {canEdit && (
            <Button
              variant="ghost"
              startIcon={<FlaskConical size={14} />}
              onClick={() => navigate('/rnc/test')}
            >
              Test send to me
            </Button>
          )}
        </section>
      </div>
    </div>
  );
}
